import type { Options, Point } from "highcharts";
import { renderToString } from "react-dom/server";

import { combineConfigs } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";

import { DomainTooltip } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/domain-influence/domains-map/components/DomainTooltip";
import { OthersTooltip } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/domain-influence/domains-map/components/OthersTooltip";
import {
    DOMAIN_TOOLTIP_FILTER_BUTTON_ID,
    OTHERS_NODE_REGEX,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/domain-influence/domains-map/consts";
import type { IFormatterContext } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/domain-influence/domains-map/types";
import { allTrackers } from "app/services/track/track";

export const getChartConfig = (onDomainFilter: (topic: string, domain: string) => void): Options =>
    combineConfigs({}, [
        {
            plotOptions: {
                treemap: {
                    point: {
                        events: {
                            click: function (this: Point & IFormatterContext) {
                                const {
                                    id,
                                    name,
                                    node: { level },
                                } = this;
                                const isTopicNode = level === 1;
                                const isOthersNode = OTHERS_NODE_REGEX.test(id);
                                const trackingData = {
                                    subName: "",
                                    subSubName: "",
                                };

                                if (isTopicNode) {
                                    trackingData.subName = "Topic";
                                    trackingData.subSubName = name;
                                } else {
                                    if (isOthersNode) {
                                        trackingData.subName = "Others";
                                    } else {
                                        trackingData.subName = "Domain";
                                        trackingData.subSubName = name;
                                    }
                                }

                                const { subName: rawSubName, subSubName: rawSubSubName } = trackingData;
                                trackingData.subName = rawSubName ? `/${rawSubName}` : "";
                                trackingData.subSubName = rawSubSubName ? `/${rawSubSubName}` : "";

                                allTrackers.dispatchTrackEvent(
                                    "Button",
                                    "click",
                                    `Domains Map${trackingData.subName}${trackingData.subSubName}`,
                                );
                            },
                        },
                    },
                },
            },
            tooltip: {
                shape: "rect",
                hideDelay: 100,
                stickOnContact: true,
                shadow: {
                    color: colorsPalettes.black[0],
                    offsetX: 0,
                    offsetY: 0,
                    opacity: 0.08,
                    width: 8,
                },
                formatter: function (this: Point & IFormatterContext) {
                    const { id, customData } = this;

                    if (OTHERS_NODE_REGEX.test(id)) {
                        const { totalDomains } = customData;
                        return renderToString(<OthersTooltip domainsCount={totalDomains} />);
                    } else {
                        const { topicName, iconUrl, sourceCategory, urlCount } = customData;
                        const { name: domainName, value: sourceAuthority } = this;

                        setTimeout(() => {
                            const buttonEl = document.getElementById(DOMAIN_TOOLTIP_FILTER_BUTTON_ID);

                            if (buttonEl && !buttonEl.dataset.bound) {
                                buttonEl.dataset.bound = "1"; //prevent multiple event bounding
                                buttonEl.addEventListener("click", () => onDomainFilter(topicName, domainName));
                            }
                        }, 0);

                        return renderToString(
                            <DomainTooltip
                                domain={domainName}
                                iconUrl={iconUrl}
                                topicName={topicName}
                                sourceCategory={sourceCategory}
                                sourceAuthority={sourceAuthority}
                                urlsCount={urlCount}
                            />,
                        );
                    }
                },
            },
        },
    ]);
